import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import './CorrectionRequest.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const MIN_COST = 0.50;

function CorrectionRequestForm({ transcriptId, transcriptData, audioDuration, onClose, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [cost, setCost] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [requestId, setRequestId] = useState(null);
  const [paymentComplete, setPaymentComplete] = useState(false);

  // Calculate cost on mount
  React.useEffect(() => {
    if (audioDuration) {
      const hours = audioDuration / 3600;
      const ratePerHour = 5.00;
      const calculatedCost = Math.max(MIN_COST, hours * ratePerHour).toFixed(2);
      setCost(calculatedCost);
    }
  }, [audioDuration]);

  const handleCreateRequest = async () => {
    setIsProcessing(true);

    try {
      const response = await fetch('/api/corrections/request', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          transcriptId,
          transcriptData,
          audioDurationSeconds: audioDuration
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create correction request');
      }

      const data = await response.json();
      setClientSecret(data.clientSecret);
      setRequestId(data.correctionRequest.id);
      setCost(data.cost);
      setIsProcessing(false);
    } catch (error) {
      console.error('Error creating request:', error);
      alert(error.message);
      setIsProcessing(false);
    }
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);

    try {
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
        }
      });

      if (error) {
        throw new Error(error.message);
      }

      if (paymentIntent.status === 'succeeded') {
        setPaymentComplete(true);
        setIsProcessing(false);
        onSuccess(requestId);
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert(error.message);
      setIsProcessing(false);
    }
  };

  const formatDuration = (seconds) => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    if (mins < 60) return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
    const hrs = Math.floor(mins / 60);
    const remainMins = mins % 60;
    return remainMins > 0 ? `${hrs}h ${remainMins}m` : `${hrs}h`;
  };

  const hours = (audioDuration / 3600).toFixed(2);
  const rawCost = (parseFloat(hours) * 5.00).toFixed(2);
  const isMinimumApplied = parseFloat(rawCost) < MIN_COST;

  // Step 3: Success screen
  if (paymentComplete) {
    return (
      <div className="correction-request-modal" onClick={onClose}>
        <div className="correction-request-content correction-success" onClick={(e) => e.stopPropagation()}>
          <div className="success-icon-wrapper">
            <svg className="success-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </div>
          <h2>Request Submitted!</h2>
          <p className="correction-subtitle">
            Your transcript has been sent for professional correction. You will receive an email notification when the corrected version is ready.
          </p>
          <div className="success-details">
            <div className="success-detail-row">
              <span>Request ID</span>
              <span>#{requestId}</span>
            </div>
            <div className="success-detail-row">
              <span>Amount Paid</span>
              <span>${cost}</span>
            </div>
            <div className="success-detail-row">
              <span>Estimated Turnaround</span>
              <span>24-48 hours</span>
            </div>
          </div>
          <div className="correction-button-group">
            <button onClick={onClose} className="correction-submit-btn">
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step 1: Request overview
  if (!clientSecret) {
    return (
      <div className="correction-request-modal" onClick={onClose}>
        <div className="correction-request-content" onClick={(e) => e.stopPropagation()}>
          <button className="close-btn" onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>

          <h2>Professional Transcript Correction</h2>
          <p className="correction-subtitle">
            Expert annotators will review and perfect your transcript
          </p>

          <div className="service-features">
            <div className="feature-item">
              <span className="feature-icon">&#10003;</span>
              <span>Improved accuracy</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">&#10003;</span>
              <span>Punctuation & formatting</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">&#10003;</span>
              <span>Speaker identification</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">&#10003;</span>
              <span>Technical terminology</span>
            </div>
          </div>

          <div className="cost-card">
            <div className="cost-row">
              <span className="cost-label">Audio Duration</span>
              <span className="cost-value">{formatDuration(audioDuration)}</span>
            </div>
            <div className="cost-row">
              <span className="cost-label">Rate</span>
              <span className="cost-value">$5.00 / hour</span>
            </div>
            <div className="cost-row total">
              <span className="cost-label">Total</span>
              <span className="cost-value">${cost}</span>
            </div>
            {isMinimumApplied && (
              <div className="minimum-notice">Minimum charge of $0.50 applied</div>
            )}
          </div>

          <div className="turnaround-notice">
            <span className="turnaround-icon">&#9200;</span>
            <span>Typical turnaround: 24-48 hours</span>
          </div>

          <div className="correction-button-group">
            <button
              onClick={handleCreateRequest}
              disabled={isProcessing}
              className="correction-submit-btn"
            >
              {isProcessing ? 'Creating Request...' : `Continue to Payment - $${cost}`}
            </button>
            <button onClick={onClose} className="correction-cancel-btn">
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Payment
  return (
    <div className="correction-request-modal" onClick={onClose}>
      <div className="correction-request-content" onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose} aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        <h2>Complete Payment</h2>
        <p className="correction-subtitle">Enter your card details to submit the correction request</p>

        <div className="payment-amount-display">
          <p className="payment-amount-label">Amount Due</p>
          <p className="payment-amount-value">${cost}</p>
          <p className="payment-request-id">Request #{requestId}</p>
        </div>

        <form onSubmit={handleSubmitPayment}>
          <div className="card-element-container">
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#424770',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                    '::placeholder': {
                      color: '#aab7c4',
                    },
                  },
                  invalid: {
                    color: '#ef4444',
                  },
                },
              }}
            />
          </div>

          <div className="payment-secure-notice">
            <span>&#128274;</span>
            <span>Secure payment powered by Stripe</span>
          </div>

          <div className="correction-button-group">
            <button
              type="submit"
              disabled={!stripe || isProcessing}
              className="correction-submit-btn"
            >
              {isProcessing ? 'Processing Payment...' : `Pay $${cost}`}
            </button>
            <button type="button" onClick={onClose} className="correction-cancel-btn">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CorrectionRequest({ transcriptId, transcriptData, audioDuration, onClose, onSuccess }) {
  // Check if feature is enabled
  const isEnabled = process.env.REACT_APP_ENABLE_HUMAN_CORRECTION === 'true';

  if (!isEnabled) {
    return null;
  }

  return (
    <Elements stripe={stripePromise}>
      <CorrectionRequestForm
        transcriptId={transcriptId}
        transcriptData={transcriptData}
        audioDuration={audioDuration}
        onClose={onClose}
        onSuccess={onSuccess}
      />
    </Elements>
  );
}

export default CorrectionRequest;
